import React, { useId } from 'react';
import { useMotorSound } from './useMotorSound';

export interface PumpSvgProps {
  isRunning?: boolean | undefined;
  isOverload?: boolean | undefined;
  color?: string | undefined; // Motor & pump body color (default: '#1e40af' industrial blue)
  casingColor?: string | undefined; // Optional custom pump volute color
  speed?: number | undefined;
  width?: string | number | undefined;
  height?: string | number | undefined;
  className?: string | undefined;
  style?: React.CSSProperties | undefined;
}

export const PumpSvg: React.FC<PumpSvgProps> = ({
  isRunning = false,
  isOverload = false,
  color = '#1e40af',
  casingColor,
  width = '100%',
  height = '100%',
  className,
  style,
}) => {
  const uid = useId().replace(/:/g, '_');
  const { isSpinning, isStopping } = useMotorSound(isRunning);
  const isPumping = isRunning || isSpinning;

  // Overload uses red/danger tone, otherwise customizable motor color
  const effectiveMotorColor = isOverload ? '#b91c1c' : color;
  const effectiveVoluteColor = casingColor || (isOverload ? '#991b1b' : color);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 120 100"
      width={width}
      height={height}
      className={className}
      style={{ overflow: 'visible', ...style }}
    >
      <defs>
        {/* Motor Stator 3D Cylindrical Gradient */}
        <linearGradient id={`motorCylinder-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="rgba(255, 255, 255, 0.45)" />
          <stop offset="25%" stopColor={effectiveMotorColor} />
          <stop offset="70%" stopColor={effectiveMotorColor} />
          <stop offset="100%" stopColor="rgba(0, 0, 0, 0.6)" />
        </linearGradient>

        {/* Specular Highlight Streak for Motor Shell */}
        <linearGradient id={`specularStreak-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="rgba(255, 255, 255, 0.6)" />
          <stop offset="50%" stopColor="rgba(255, 255, 255, 0.15)" />
          <stop offset="100%" stopColor="rgba(255, 255, 255, 0)" />
        </linearGradient>

        {/* Pump Volute 3D Radial Gradient */}
        <radialGradient id={`voluteGrad-${uid}`} cx="38%" cy="38%" r="65%">
          <stop offset="0%" stopColor="rgba(255, 255, 255, 0.4)" />
          <stop offset="40%" stopColor={effectiveVoluteColor} />
          <stop offset="85%" stopColor={effectiveVoluteColor} />
          <stop offset="100%" stopColor="#0f172a" />
        </radialGradient>

        {/* Metal Flange Collar Gradient */}
        <linearGradient id={`flangeGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#cbd5e1" />
          <stop offset="45%" stopColor="#64748b" />
          <stop offset="100%" stopColor="#334155" />
        </linearGradient>

        {/* Pipe Barrel Horizontal Gradient */}
        <linearGradient id={`pipeHoriz-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#94a3b8" />
          <stop offset="35%" stopColor="#cbd5e1" />
          <stop offset="70%" stopColor="#475569" />
          <stop offset="100%" stopColor="#1e293b" />
        </linearGradient>

        {/* Pipe Barrel Vertical Gradient */}
        <linearGradient id={`pipeVert-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#1e293b" />
          <stop offset="30%" stopColor="#94a3b8" />
          <stop offset="60%" stopColor="#475569" />
          <stop offset="100%" stopColor="#0f172a" />
        </linearGradient>

        {/* Base Skid Cast Iron Gradient */}
        <linearGradient id={`baseSkid-${uid}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#475569" />
          <stop offset="40%" stopColor="#334155" />
          <stop offset="100%" stopColor="#0f172a" />
        </linearGradient>

        {/* Impeller Brass Center Cap */}
        <radialGradient id={`impellerHub-${uid}`} cx="35%" cy="35%" r="60%">
          <stop offset="0%" stopColor="#fef08a" />
          <stop offset="60%" stopColor="#ca8a04" />
          <stop offset="100%" stopColor="#713f12" />
        </radialGradient>

        {/* Pressure Gauge Glow Filter */}
        <filter id={`gaugeGlow-${uid}`} x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="1" stdDeviation="0.8" floodColor="#000" floodOpacity="0.4" />
        </filter>
      </defs>

      <style>{`
        @keyframes pump-spin-${uid} {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .pump-impeller-${uid} {
          transform-origin: 42px 56px;
          ${isPumping ? `animation: pump-spin-${uid} ${isStopping ? '1.5s' : '0.7s'} linear infinite;` : ''}
        }
        @keyframes pump-vibe-${uid} {
          0% { transform: translate(0, 0); }
          25% { transform: translate(-0.2px, 0.2px); }
          50% { transform: translate(0.2px, -0.2px); }
          75% { transform: translate(-0.2px, -0.2px); }
          100% { transform: translate(0, 0); }
        }
        .pump-vibrating-${uid} {
          ${isPumping ? `animation: pump-vibe-${uid} 0.08s linear infinite;` : ''}
        }
      `}</style>

      {/* Ground Cast Shadow */}
      <ellipse cx="60" cy="91" rx="52" ry="4" fill="rgba(0, 0, 0, 0.45)" />

      {/* Main Container with Vibration when Running */}
      <g className={`pump-vibrating-${uid}`}>
        {/* --- BASE SKID (Ortak Çelik Montaj Şasisi) --- */}
        <rect x="8" y="80" width="104" height="9" rx="2" fill={`url(#baseSkid-${uid})`} stroke="#0f172a" strokeWidth="1" />
        {/* Base Skid Top Highlight */}
        <line x1="9" y1="80.8" x2="111" y2="80.8" stroke="rgba(255, 255, 255, 0.3)" strokeWidth="0.8" />
        {/* Anchor Foundation Bolts */}
        <circle cx="14" cy="85" r="1.3" fill="#cbd5e1" stroke="#1e293b" strokeWidth="0.5" />
        <circle cx="60" cy="85" r="1.3" fill="#cbd5e1" stroke="#1e293b" strokeWidth="0.5" />
        <circle cx="106" cy="85" r="1.3" fill="#cbd5e1" stroke="#1e293b" strokeWidth="0.5" />

        {/* --- SUCTION INLET PIPE (Sol Emiş Borusu Girişi) --- */}
        {/* Horizontal Pipe Neck */}
        <rect
          x="7"
          y="52"
          width="18"
          height="8"
          fill={`url(#pipeHoriz-${uid})`}
          stroke="#0f172a"
          strokeWidth="0.8"
        />
        {/* Suction Flange Collar (Sol Dikey Flanş) */}
        <rect
          x="4"
          y="48.5"
          width="4.5"
          height="15"
          rx="1.5"
          fill={`url(#flangeGrad-${uid})`}
          stroke="#0f172a"
          strokeWidth="0.8"
        />
        {/* Flange Bolts */}
        <circle cx="6.25" cy="51" r="0.9" fill="#f8fafc" stroke="#334155" strokeWidth="0.4" />
        <circle cx="6.25" cy="54.5" r="0.9" fill="#f8fafc" stroke="#334155" strokeWidth="0.4" />
        <circle cx="6.25" cy="57.5" r="0.9" fill="#f8fafc" stroke="#334155" strokeWidth="0.4" />
        <circle cx="6.25" cy="61" r="0.9" fill="#f8fafc" stroke="#334155" strokeWidth="0.4" />
        {/* Suction Pipe Hole Orifice (Inlet Port) */}
        <ellipse cx="4" cy="56" rx="1.4" ry="4.5" fill="#090d16" stroke="#475569" strokeWidth="0.6" />
        <ellipse cx="4" cy="56" rx="0.9" ry="3" fill="#020617" />
        {/* Pipe Connection Port Marker */}
        <circle cx="4" cy="56" r="1.3" fill="#38bdf8" opacity="0.4" />

        {/* --- DISCHARGE OUTLET PIPE (Üst Basma Borusu Çıkışı) --- */}
        {/* Vertical Pipe Neck */}
        <rect
          x="38"
          y="8"
          width="8"
          height="24"
          fill={`url(#pipeVert-${uid})`}
          stroke="#0f172a"
          strokeWidth="0.8"
        />
        {/* Discharge Flange Collar (Üst Yatay Flanş) */}
        <rect
          x="34.5"
          y="4"
          width="15"
          height="4.5"
          rx="1.5"
          fill={`url(#flangeGrad-${uid})`}
          stroke="#0f172a"
          strokeWidth="0.8"
        />
        {/* Flange Bolts */}
        <circle cx="37" cy="6.25" r="0.9" fill="#f8fafc" stroke="#334155" strokeWidth="0.4" />
        <circle cx="40.5" cy="6.25" r="0.9" fill="#f8fafc" stroke="#334155" strokeWidth="0.4" />
        <circle cx="43.5" cy="6.25" r="0.9" fill="#f8fafc" stroke="#334155" strokeWidth="0.4" />
        <circle cx="47" cy="6.25" r="0.9" fill="#f8fafc" stroke="#334155" strokeWidth="0.4" />
        {/* Discharge Pipe Hole Orifice (Outlet Port) */}
        <ellipse cx="42" cy="4" rx="4.5" ry="1.4" fill="#090d16" stroke="#475569" strokeWidth="0.6" />
        <ellipse cx="42" cy="4" rx="3" ry="0.9" fill="#020617" />
        {/* Pipe Connection Port Marker */}
        <circle cx="42" cy="4" r="1.3" fill="#38bdf8" opacity="0.4" />

        {/* --- PRESSURE GAUGE (Basınç Manometresi) --- */}
        <g filter={`url(#gaugeGlow-${uid})`}>
          {/* Gauge Mount Stem */}
          <rect x="52" y="16" width="3.5" height="10" fill="#cbd5e1" stroke="#334155" strokeWidth="0.6" />
          {/* Gauge Circular Dial Body */}
          <circle cx="53.5" cy="14" r="7" fill="#0f172a" stroke="#64748b" strokeWidth="1" />
          <circle cx="53.5" cy="14" r="5.8" fill="#f8fafc" />
          {/* Scale Markings */}
          <path d="M 49 16 A 5 5 0 1 1 58 16" fill="none" stroke="#94a3b8" strokeWidth="0.8" strokeDasharray="1,1" />
          {/* Pressure Needle (Rotates when running) */}
          <line
            x1="53.5"
            y1="14"
            x2={isRunning ? '57' : '50'}
            y2={isRunning ? '11' : '15'}
            stroke="#ef4444"
            strokeWidth="0.9"
            strokeLinecap="round"
          />
          <circle cx="53.5" cy="14" r="1.2" fill="#1e293b" />
        </g>

        {/* --- CENTRIFUGAL VOLUTE CASING (Salyangoz Pompa Gövdesi) --- */}
        {/* Outer Spiral Volute Body */}
        <circle
          cx="42"
          cy="56"
          r="24"
          fill={`url(#voluteGrad-${uid})`}
          stroke="#0f172a"
          strokeWidth="1.2"
        />

        {/* Volute Casing Ring Highlight */}
        <circle cx="42" cy="56" r="21.5" fill="none" stroke="rgba(255, 255, 255, 0.25)" strokeWidth="1" />

        {/* Casing Flange Assembly Bolts */}
        <circle cx="23" cy="56" r="1.2" fill="#e2e8f0" stroke="#1e293b" strokeWidth="0.5" />
        <circle cx="61" cy="56" r="1.2" fill="#e2e8f0" stroke="#1e293b" strokeWidth="0.5" />
        <circle cx="42" cy="37" r="1.2" fill="#e2e8f0" stroke="#1e293b" strokeWidth="0.5" />
        <circle cx="42" cy="75" r="1.2" fill="#e2e8f0" stroke="#1e293b" strokeWidth="0.5" />
        <circle cx="28" cy="43" r="1.2" fill="#e2e8f0" stroke="#1e293b" strokeWidth="0.5" />
        <circle cx="56" cy="69" r="1.2" fill="#e2e8f0" stroke="#1e293b" strokeWidth="0.5" />
        <circle cx="28" cy="69" r="1.2" fill="#e2e8f0" stroke="#1e293b" strokeWidth="0.5" />
        <circle cx="56" cy="43" r="1.2" fill="#e2e8f0" stroke="#1e293b" strokeWidth="0.5" />

        {/* Volute Foot Mounting Bracket */}
        <rect x="36" y="75" width="12" height="6" fill="#1e293b" stroke="#0f172a" strokeWidth="0.8" />

        {/* Inner Impeller Eye / Glass View Chamber */}
        <circle cx="42" cy="56" r="13" fill="#090d16" stroke="#334155" strokeWidth="1.2" />

        {/* --- DYNAMIC ROTATING IMPELLER (Dönen Çark) --- */}
        <g className={`pump-impeller-${uid}`}>
          {/* Curved Impeller Vanes (6 Kavisli Kanat) */}
          <path d="M 42 56 Q 48 48 42 45" stroke="#38bdf8" strokeWidth="2.4" strokeLinecap="round" fill="none" opacity="0.9" />
          <path d="M 42 56 Q 52 52 53 58" stroke="#38bdf8" strokeWidth="2.4" strokeLinecap="round" fill="none" opacity="0.9" />
          <path d="M 42 56 Q 46 64 41 67" stroke="#38bdf8" strokeWidth="2.4" strokeLinecap="round" fill="none" opacity="0.9" />
          <path d="M 42 56 Q 36 64 31 59" stroke="#38bdf8" strokeWidth="2.4" strokeLinecap="round" fill="none" opacity="0.9" />
          <path d="M 42 56 Q 32 50 34 46" stroke="#38bdf8" strokeWidth="2.4" strokeLinecap="round" fill="none" opacity="0.9" />
          <path d="M 42 56 Q 40 46 45 45" stroke="#38bdf8" strokeWidth="2.4" strokeLinecap="round" fill="none" opacity="0.9" />
          {/* Central Shaft Nut / Hub */}
          <circle cx="42" cy="56" r="4.2" fill={`url(#impellerHub-${uid})`} stroke="#1e293b" strokeWidth="0.8" />
          <circle cx="42" cy="56" r="1.8" fill="#ca8a04" />
        </g>

        {/* Inspection Glass Cover Glare Streak */}
        <path d="M 33 49 Q 42 44 51 51" stroke="rgba(255, 255, 255, 0.4)" strokeWidth="1.2" fill="none" strokeLinecap="round" />

        {/* --- SHAFT LANTERN / COUPLING HOUSING (Kaplin ve Mil Muhafazası) --- */}
        <rect x="58" y="47" width="8" height="18" fill="#334155" stroke="#0f172a" strokeWidth="0.8" />
        <rect x="60" y="50" width="4" height="12" rx="1" fill="#0f172a" />
        <line x1="62" y1="50" x2="62" y2="62" stroke="#94a3b8" strokeWidth="1.5" />

        {/* --- 3D ELECTRIC DRIVE MOTOR (Elektrik Motoru) --- */}
        {/* Motor Mounting Feet */}
        <rect x="71" y="74" width="7" height="7" fill="#1e293b" stroke="#0f172a" strokeWidth="0.8" />
        <rect x="95" y="74" width="7" height="7" fill="#1e293b" stroke="#0f172a" strokeWidth="0.8" />

        {/* Stator Cylindrical Housing Body */}
        <rect
          x="65"
          y="38"
          width="40"
          height="37"
          rx="3"
          fill={`url(#motorCylinder-${uid})`}
          stroke="#0f172a"
          strokeWidth="1.2"
        />

        {/* Specular Cylindrical Reflection Line */}
        <rect
          x="66"
          y="40"
          width="38"
          height="8"
          fill={`url(#specularStreak-${uid})`}
          pointerEvents="none"
        />

        {/* Cooling Fins (Soğutma Kanatçıkları) */}
        <line x1="66" y1="44" x2="104" y2="44" stroke="#0f172a" strokeWidth="1.2" />
        <line x1="66" y1="44.8" x2="104" y2="44.8" stroke="rgba(255, 255, 255, 0.35)" strokeWidth="0.7" />

        <line x1="66" y1="51" x2="104" y2="51" stroke="#0f172a" strokeWidth="1.2" />
        <line x1="66" y1="51.8" x2="104" y2="51.8" stroke="rgba(255, 255, 255, 0.35)" strokeWidth="0.7" />

        <line x1="66" y1="58" x2="104" y2="58" stroke="#0f172a" strokeWidth="1.2" />
        <line x1="66" y1="58.8" x2="104" y2="58.8" stroke="rgba(255, 255, 255, 0.35)" strokeWidth="0.7" />

        <line x1="66" y1="65" x2="104" y2="65" stroke="#0f172a" strokeWidth="1.2" />
        <line x1="66" y1="65.8" x2="104" y2="65.8" stroke="rgba(255, 255, 255, 0.35)" strokeWidth="0.7" />

        <line x1="66" y1="71" x2="104" y2="71" stroke="#0f172a" strokeWidth="1.2" />
        <line x1="66" y1="71.8" x2="104" y2="71.8" stroke="rgba(255, 255, 255, 0.35)" strokeWidth="0.7" />

        {/* --- TERMINAL BOX (Klemens Bağlantı Kutusu) --- */}
        <rect
          x="75"
          y="27"
          width="18"
          height="12"
          rx="2"
          fill="#1e293b"
          stroke="#0f172a"
          strokeWidth="1"
        />
        <rect x="76.5" y="28" width="15" height="3" fill="#334155" />
        <circle cx="77.5" cy="29.5" r="0.7" fill="#cbd5e1" />
        <circle cx="89.5" cy="29.5" r="0.7" fill="#cbd5e1" />
        {/* Cable Gland Nipple */}
        <rect x="93" y="31" width="3.5" height="5" rx="1" fill="#475569" stroke="#0f172a" strokeWidth="0.6" />

        {/* --- REAR FAN COWL (Arka Fan Kapağı) --- */}
        <path
          d="M 105 39 C 109 40 110 46 110 56.5 C 110 67 109 73 105 74 Z"
          fill="#1e293b"
          stroke="#0f172a"
          strokeWidth="1"
        />
        {/* Fan Air Grille Slots */}
        <line x1="107" y1="47" x2="107" y2="66" stroke="#0f172a" strokeWidth="1.2" />
        <line x1="108.5" y1="51" x2="108.5" y2="62" stroke="#0f172a" strokeWidth="1.2" />

        {/* --- RUNNING / STATUS PILOT LED INDICATOR --- */}
        <circle
          cx="84"
          cy="34"
          r="1.8"
          fill={isOverload ? '#ef4444' : (isRunning ? '#22c55e' : '#475569')}
          stroke="#0f172a"
          strokeWidth="0.5"
        />
        {isRunning && (
          <circle
            cx="84"
            cy="34"
            r="3"
            fill="none"
            stroke={isOverload ? '#ef4444' : '#22c55e'}
            strokeWidth="0.6"
            opacity="0.8"
          />
        )}
      </g>
    </svg>
  );
};
